import { CustomFieldHint } from "@/components/custom-field/CustomFieldHint.tsx";
import { Datepicker } from "@/components/datepicker/Datepicker.tsx";
import { RichEditor } from "@/components/rich-editor/RichEditor.tsx";
import { CurrencySelect } from "@/components/select/CurrencySelect.tsx";
import useCustomFieldsHook from "@/hooks/use-custom-fields.hook.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import useEmployeeSearchInline from "@/queries/employees/use-employees.query.ts";
import { useOffersSearch } from "@/queries/recruit/use-offer.query.ts";
import { useLegalEntities } from "@/queries/use-legal-entities.query.ts";
import { useLocations } from "@/queries/use-locations.query.ts";
import { ApiField, ApiFieldTypes } from "@/types/custom-field.types.ts";
import {
  ApiCompensationTypeEnum,
  getOfferSalaryTypes,
  useOfferEditContext,
} from "@/types/recruit/offer.types.ts";
import { Box, Input, Select, SelectItem, Stack, Title } from "@skailer-com/uikit";
import { ReactNode, useMemo } from "react";
import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";

type OfferFieldProps = { label: string; required: boolean; placeholder?: string };

export function OfferEditField({ field }: { field: ApiField }) {
  const { getFieldTitle, getFieldByType } = useCustomFieldsHook();
  const { formHook, offerRequest } = useOfferEditContext();
  const props: OfferFieldProps = {
    label: getFieldTitle(field),
    required: field.is_mandatory,
    placeholder: field.description.trim() || undefined,
  };

  if (field.type === ApiFieldTypes.custom) {
    return getFieldByType({
      field,
      formHook,
      canView: true,
      canEdit: true,
      fieldValue:
        offerRequest?.offer_data.custom_fields?.find((item) => item.field.id === field.id)?.value ??
        undefined,
    });
  }

  return <CustomFieldHint hint={field.hint}>{getSystemField(field, props)}</CustomFieldHint>;
}

function getSystemField(field: ApiField, props: OfferFieldProps): ReactNode {
  switch (field.type) {
    case ApiFieldTypes.offer_first_name:
      return <OfferTextField name="first_name" {...props} />;
    case ApiFieldTypes.offer_last_name:
      return <OfferTextField name="last_name" {...props} />;
    case ApiFieldTypes.offer_job_title:
      return <OfferTextField name="job_title" {...props} />;
    case ApiFieldTypes.offer_date_start:
      return <OfferDateStartField {...props} />;
    case ApiFieldTypes.offer_legal_entity:
      return <OfferLegalEntityField {...props} />;
    case ApiFieldTypes.offer_location:
      return <OfferLocationField {...props} />;
    case ApiFieldTypes.offer_manager:
      return <OfferManagerField {...props} />;
    case ApiFieldTypes.offer_salary:
      return <OfferSalaryField {...props} />;
    case ApiFieldTypes.offer_text:
      return <OfferRichTextField {...props} />;
    default:
      return null;
  }
}

function OfferTextField({
  name,
  label,
  required,
  placeholder,
}: OfferFieldProps & { name: "first_name" | "last_name" | "job_title" }) {
  const { formHook } = useOfferEditContext();
  return (
    <Controller
      control={formHook.control}
      name={name}
      render={({ field, fieldState }) => (
        <Input
          {...field}
          label={label}
          placeholder={placeholder}
          value={field.value ?? ""}
          onChange={field.onChange}
          error={fieldState.error?.message}
          type={"text"}
          required={required}
        />
      )}
    />
  );
}

function OfferDateStartField({ label, required }: OfferFieldProps) {
  const { formHook } = useOfferEditContext();
  return (
    <Controller
      control={formHook.control}
      name="date_start"
      render={({ field, fieldState }) => (
        <Datepicker
          {...field}
          label={label}
          required={required}
          error={fieldState.error?.message}
        />
      )}
    />
  );
}

function OfferLegalEntityField({ label, required, placeholder }: OfferFieldProps) {
  const { t } = useTranslation();
  const { formHook } = useOfferEditContext();
  const { data: legalEntities, isLoading } = useLegalEntities();
  const options = useMemo(
    () => legalEntities?.map((x): SelectItem => ({ id: x.id, title: x.title })) ?? [],
    [legalEntities]
  );
  return (
    <Controller
      control={formHook.control}
      name="legal_entity_id"
      render={({ field, fieldState }) => (
        <Select
          {...field}
          label={label}
          required={required}
          options={options}
          isLoading={isLoading}
          error={fieldState.error?.message}
          emptyTitle={placeholder ?? t("common:select")}
          isClearable
        />
      )}
    />
  );
}

function OfferLocationField({ label, required, placeholder }: OfferFieldProps) {
  const { t } = useTranslation();
  const { formHook } = useOfferEditContext();
  const { data: locations, isLoading } = useLocations();
  const options = useMemo(
    () => locations?.map((x): SelectItem => ({ id: x.id, title: x.title })) ?? [],
    [locations]
  );
  return (
    <Controller
      control={formHook.control}
      name="location_id"
      render={({ field, fieldState }) => (
        <Select
          {...field}
          label={label}
          required={required}
          options={options}
          isLoading={isLoading}
          error={fieldState.error?.message}
          emptyTitle={placeholder ?? t("common:select")}
          isClearable
        />
      )}
    />
  );
}

function OfferManagerField({ label, required, placeholder }: OfferFieldProps) {
  const { t } = useTranslation();
  const { formHook } = useOfferEditContext();
  const managerSearch = useEmployeeSearchInline();
  return (
    <Controller
      control={formHook.control}
      name="manager_id"
      render={({ field, fieldState }) => (
        <Select
          {...field}
          label={label}
          required={required}
          options={managerSearch.options}
          isLoading={managerSearch.isLoading}
          searchable
          showAvatar={true}
          showCaption={true}
          onSearchOptions={(text) => managerSearch.search(text)}
          searchOptions={managerSearch.options}
          loadOptionsOnOpen
          error={fieldState.error?.message}
          emptyTitle={placeholder ?? t("common:select")}
          isClearable
        />
      )}
    />
  );
}

function OfferSalaryField({ label, required }: OfferFieldProps) {
  const { t } = useTranslation();
  const { formHook } = useOfferEditContext();
  const watchedSalaryType = formHook.watch("salary.salary_type");
  return (
    <Stack gap={"sm"} className="grid grid-cols-3">
      <Controller
        control={formHook.control}
        name="salary.salary_type"
        render={({ field, fieldState }) => (
          <Select
            {...field}
            isClearable
            label={t("common:type")}
            required={required}
            options={getOfferSalaryTypes()}
            error={fieldState.error?.message}
            emptyTitle={t("common:select")}
          />
        )}
      />
      {watchedSalaryType === ApiCompensationTypeEnum.custom ? (
        <Box className="col-span-2">
          <Controller
            control={formHook.control}
            name="salary.salary_value"
            render={({ field, fieldState }) => (
              <Input
                {...field}
                value={field.value ?? ""}
                type="text"
                label={t("common:details")}
                error={fieldState.error?.message}
              />
            )}
          />
        </Box>
      ) : (
        <>
          <Controller
            control={formHook.control}
            name="salary.currency_code"
            render={({ field, fieldState }) => (
              <CurrencySelect
                {...field}
                label={t("common:currency")}
                emptyTitle={t("common:select")}
                searchable
                error={fieldState.error?.message}
              />
            )}
          />
          <Controller
            control={formHook.control}
            name="salary.salary_value"
            render={({ field, fieldState }) => (
              <Input
                {...field}
                value={field.value ?? ""}
                type="text"
                inputMode="decimal"
                label={label}
                error={fieldState.error?.message}
              />
            )}
          />
        </>
      )}
    </Stack>
  );
}

interface SelectOfferTemplate extends SelectItem {
  caption: string;
  metadata: {
    offerText: string;
  };
}

function OfferRichTextField({ label, required, placeholder }: OfferFieldProps) {
  const { t } = useTranslation();
  const { renderDate } = useFormatter();
  const { employeeId, formHook } = useOfferEditContext();
  const myOffers = useOffersSearch(
    {
      created_by_ids: employeeId ? [employeeId] : [],
      page_size: 5,
      page_number: 1,
    },
    !!employeeId
  );

  const offerTemplates = useMemo(
    (): SelectOfferTemplate[] =>
      myOffers.data?.items
        .filter((offer) => offer.offer_data.offer_text && offer.offer_data.offer_text.trim() !== "")
        .map((offer) => ({
          id: offer.offer_data.id,
          title: offer.offer_data.job_title || "—",
          caption: `${offer.offer_data.first_name} ${offer.offer_data.last_name} · ${renderDate(offer.offer_data.datetime_created_at)}`,
          metadata: { offerText: offer.offer_data.offer_text },
        })) ?? [],
    [myOffers.data?.items, renderDate]
  );

  const handleTemplateSelection = (templateId: number) => {
    const selectedTemplate = offerTemplates.find((template) => template.id === templateId);

    formHook.setValue("offer_text", selectedTemplate ? selectedTemplate.metadata.offerText : "", {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  return (
    <Stack gap={"lg"}>
      {offerTemplates.length > 0 && (
        <Controller
          control={formHook.control}
          name="offer_template_id"
          render={({ field: { value, onChange }, fieldState }) => (
            <Select
              label={t("common:choose_template")}
              error={fieldState.error?.message}
              emptyTitle={t("common:select")}
              isClearable
              options={offerTemplates}
              showCaption
              value={value}
              onChange={(selectedTemplateId) => {
                onChange(selectedTemplateId);
                if (selectedTemplateId) {
                  handleTemplateSelection(selectedTemplateId as number);
                }
              }}
            />
          )}
        />
      )}
      <Controller
        control={formHook.control}
        name="offer_text"
        render={({ field: { value, onChange }, fieldState }) => (
          <Stack>
            <Title header={label} size={"sm"} paddingBottom required={required} />
            <RichEditor
              value={value ?? ""}
              onChange={onChange}
              placeholder={placeholder}
              hideToolbar={false}
              error={fieldState.error?.message}
            />
          </Stack>
        )}
      />
    </Stack>
  );
}
